<template>
  <div
    v-if="!removeFromDOM"
    class="jfpc"
    :class="themeClass"
    :publisher-component-id="id"
    :publisher-component-placement="componentPlacement"
    :instance-id="instanceId"
  >

    <div v-if="topProductUnavailable" v-show="!hideTopProductUnavailable" class="vehicle-listings flex justify-center">
      <tw-card
          :ref="'vehicleListingsVehicleUnavailable_' + topProductId"
          no-padding
          class="vehicle-listings__card min-w-0 cursor-pointer relative mb-4"
          @click.native="() => {}"
          :dark-mode="darkMode"
          style="background: #e3e8ee !important; border-radius: 14px;"
          flat
      >
        <icon-close
            style="position: absolute; top: 10px; right: 10px;"
            color="gray"
            size="24"
            @click.native="() => { hideTopProductUnavailable = true; }"
        />
        <div class="p-3 flex items-start gap-x-3 mr-8 flex-nowrap">
          <div>
            <div style="color: #4b5563 !important;">The {{ (urlParams.get('make') !== null && urlParams.get('make').toString().length > 0 ? urlParams.get('make') : 'vehicle') }} from your ad is <b>no longer listed</b>. Here are similar vehicles near you in {{ searchLocationFormatted }}.</div>
          </div>
        </div>
      </tw-card>
    </div>
    <div v-else-if="topProductOutOfRange" v-show="!hideTopProductOutOfRange" class="vehicle-listings flex justify-center">
      <tw-card
        :ref="'vehicleListingsVehicleOutOfRange_' + topProductId"
        no-padding
        class="vehicle-listings__card min-w-0 cursor-pointer relative mb-4"
        @click.native="() => {}"
        :dark-mode="darkMode"
        style="background: #e3e8ee !important; border-radius: 14px;"
        flat
      >
        <icon-close
          style="position: absolute; top: 10px; right: 10px;"
          color="gray"
          size="24"
          @click.native="() => { hideTopProductOutOfRange = true; }"
        />
        <div class="p-3 flex items-start gap-x-3 mr-8 flex-nowrap">
          <div style="width: 54px; min-width: 54px; border-radius: 7px; overflow: hidden">
            <tw-image :src="listings[0].images[0]" :aspect-ratio="4/3" style="border-radius: 14px; opacity: 0.65" />
          </div>
          <div>
            <div style="margin-top: -2px; color: #4b5563 !important;">The {{ listings[0].make }} from your ad is <b>{{ listings[0].distance }} mi away</b> {{ (listings[0].dealer.city.length > 0 ? (' in ' + listings[0].dealer.city + ', ' + listings[0].dealer.state) : '') }}. Here are similar vehicles near you in {{ searchLocationFormatted }}.</div>
            <div @click="() => { topProductOutOfRange = false; }" class="mt-0.5" style="cursor: pointer; text-decoration: underline; color: #6c7280; font-weight: 500">View it anyway</div>
          </div>
        </div>
      </tw-card>
    </div>

    <div class="header-text mb-4" :class="{ 'text-white': darkMode }">
      {{ headerText }}
      <div v-if="!loading && listings.length === 0" class="disclaimer-text text-sm font-normal">
        No listings found in your area.
      </div>
    </div>

    <div v-if="listings.length > 0" class="vehicle-listings flex justify-center grid gap-4 grid-cols-1 md:grid-cols-2">
      <div v-for="(vehicle, i) in listings" :key="i + '_vehicleListing'" v-show="!(i === 0 && topProductOutOfRange)">
        <tw-card
            :ref="'vehicleListingsVehicle_' + i"
            no-padding
            class="vehicle-listings__card min-w-0 cursor-pointer"
            :class="{
          'border-primary border-3': urlParams.get('fpid') == 1 && topProductId == vehicle.product_id
        }"
            @click.native="() => { if(urlParams.get('clkCnfm') == 1 && vehicle.conversion_goal_event_type_id == 3 && !isVdpInterstitial(vehicle)) { promptConfirmationModal(vehicle); } else { navigateToVDP(vehicle); } }"
            :dark-mode="darkMode"
        >
          <div v-if="urlParams.get('fpid') == 1 && topProductId == vehicle.product_id" class="p-2.5 bg-primary text-white text-sm" style="margin-bottom: -5px; z-index: 10; position: relative">
            Here's the <b>{{ vehicle.title }}</b> you were looking at.
          </div>
          <div class="relative">
            <div v-if="featuredListingProductIds.includes(vehicle.product_id)" class="absolute left-2 top-2 z-10">
              <div
                class="inline-flex items-center rounded-full font-semibold"
                style="gap: 3px; padding: 4px 8px; font-size: 11.5px; color: #f1f1f1; background-color: #3d4449; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2)"
              >
                <icon-star-filled size="15" style="margin-left: -1px; color: #f59e0c" />
                <div style="line-height: 1; font-size: 12px">Featured Listings</div>
              </div>
            </div>
            <TwImageCarousel
              :images="vehicle.images"
              :aspect-ratio="4/3"
              style="border-top-left-radius: 7px; border-top-right-radius: 7px"
              :style="[ urlParams.get('fpid') == 1 && topProductId == vehicle.product_id ? { 'border-radius-color': '#fff' } : {} ]"
              hide-count
            />
            <tw-score-badge
              v-if="urlParams.get('scrBdg') == 1 && vehicle.deal_score != null && vehicle.deal_score !== ''"
              :score="vehicle.deal_score"
              class="absolute left-2 bottom-2 z-10"
              style="box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2)"
            />
          </div>
          <div class="pb-2 pt-2 px-3">
            <p v-html="vehicle.title_raw" class="font-bold text-lg truncate" style="font-size: 18px" />
            <div class="flex justify-between items-end space-x-4 mt-1">
              <div class="flex flex-col items-start flex-shrink-0 min-w-0 text-primary">
                <h4 class="text-xl font-bold" style="font-size: 23px">{{ vehicle.price_formatted }}</h4>
                <span class="flex items-center font-medium text-sm" style="white-space: nowrap; font-size: 0.775rem">
                <tw-tooltip-new width="250px" anchor="right bottom">
                  <div class="flex items-center">
                    Est. ${{ vehicle.estimated_monthly_payment }}/mo
                    <icon-help size="12" class="ml-0.5" />
                  </div>
                  <template v-slot:tooltip-content>
                    <div>Monthly payment estimate of ${{ vehicle.estimated_monthly_payment }} is based on a 72 month loan at 7.8% APR with a 20% down payment. Estimated payments are for informational purposes only. They do not account for financing pre-qualifications, and they may or may not account for acquisition fees, destination charges, tax, title, and other fees and incentives or represent a financing offer or guarantee of credit from the seller.</div>
                  </template>
                </tw-tooltip-new>
              </span>
              </div>
              <div class="flex flex-col items-end flex-shrink-0 min-w-0 ">
                <span v-html="vehicle.mileage_formatted + ' miles'" class="truncate font-medium " />
                <div v-if="vehicle.dealer.city.length > 0 || vehicle.distance" class="flex items-center justify-center truncate text-sm text-gray-500">
                  <icon-map-marker class="text-gray-500 mr-0.5" size="13" style="margin-top: -1px" />
                  <span v-html="('distance' in vehicle ? (vehicle.distance + ' mi away') : (vehicle.dealer.city.length > 0 ? (vehicle.dealer.city + ', ' + vehicle.dealer.state) : '-'))" class="truncate"></span>
                </div>
              </div>
            </div>
            <div v-if="urlParams.get('fpid') == 1 && urlParams.get('fpidM') == 1 && topProductId == vehicle.product_id">
              <div class="pt-2 mt-2 font-medium flex items-center flex-wrap gap-x-2 gap-y-0.5 text-sm" style="border-top: 1px solid #d9dbdf;">
                <div class="flex items-center flex-nowrap truncate" style="width: 48%">
                  <div
                      style="border-radius: 100%; width: 14px; height: 14px; margin-top: -1px"
                      :style="{ 'background': getDealScoreColor(vehicle.deal_score_phrase) }"
                      class="mr-1.5 flex items-center justify-center"
                  >
                    <component :is="getDealScoreArrow(vehicle.deal_score_phrase)" class="text-white" size="12" />
                  </div>
                  <div class="truncate">{{ vehicle.deal_score_phrase.charAt(0).toUpperCase() + vehicle.deal_score_phrase.slice(1) }} Deal</div>
                </div>
                <div class="flex items-center flex-nowrap truncate" style="width: 48%">
                  <icon-steering-wheel class="text-gray-500 mr-1.5" size="15" />
                  <div class="truncate">{{ {
                    AWD: "All-Wheel Drive",
                    FWD: "Front-Wheel Drive",
                    RWD: "Rear Wheel Drive"
                  }[vehicle.drivetrain] }}</div>
                </div>
                <div class="flex items-center flex-nowrap truncate" style="width: 48%">
                  <icon-transmission class="text-gray-500 mr-1.5" size="15" />
                  <div class="truncate">{{ vehicle.transmission }}</div>
                </div>
                <div class="flex items-center flex-nowrap truncate" style="width: 48%">
                  <icon-gas-pump class="text-gray-500 mr-1.5" size="15" />
                  <div class="truncate">{{ (!vehicle.highway_mpg || vehicle.highway_mpg &lt;= 1 ? "N/A" : vehicle.highway_mpg) }} MPG {{ vehicle.fuel_type ? " (" + vehicle.fuel_type + ")" : "" }}</div>
                </div>
                <div class="flex items-center flex-nowrap truncate" style="width: 48%">
                  <div class="mr-1.5" style="width: 14px; height: 14px; border-radius: 50%;" :style="[{ 'background': vehicle.exterior_color }, (!vehicle.exterior_color || vehicle.exterior_color.toLowerCase() == 'white' || vehicle.exterior_color.toLowerCase() == 'other' ? { 'border': '1px solid #6c7280'} : {})]"></div>
                  <div class="truncate">{{ vehicle.exterior_color }} Exterior</div>
                </div>
                <div class="flex items-center flex-nowrap truncate" style="width: 48%">
                  <div class="mr-1.5" style="width: 14px; height: 14px; border-radius: 50%;" :style="[{ 'background': vehicle.interior_color }, (!vehicle.interior_color || vehicle.interior_color.toLowerCase() == 'white' || vehicle.interior_color.toLowerCase() == 'other' ? { 'border': '1px solid #6c7280'} : {})]"></div>
                  <div class="truncate">{{ vehicle.interior_color }} Interior</div>
                </div>
              </div>
              <div class="text-left mt-2 pt-2" style="border-top: 1px solid #d9dbdf;">
                <div v-if="vehicle.dealer.name.length > 0" class="flex items-center truncate flex-nowrap text-sm text-gray-600">
                  <icon-storefront class="text-gray-500 mr-1.5" size="15" />
                  <div v-html="vehicle.dealer.name" class="truncate"></div>
                </div>
                <div v-if="vehicle.dealer.city.length > 0 || vehicle.distance" class="flex items-center flex-nowrap truncate text-sm text-gray-600 mt-0.5">
                  <icon-map-marker class="text-gray-500 mr-1.5" size="15" />
                  <div v-html="(vehicle.dealer.city.length > 0 ? (vehicle.dealer.city + ', ' + vehicle.dealer.state) : (vehicle.distance + ' mi away'))" class="truncate"></div>
                </div>
              </div>
              <div class="bg-white my-2 px-4 py-2.5 flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors w-full flex-shrink-0 border-primary" style="gap: 6px; border-radius: 14px; height: fit-content;border: 1px solid;box-shadow: 0 1px 12px rgba(1, 1, 1, 0.1), 0 3px 6px rgba(1, 1, 1, 0.1), 0 3px 3px -4px rgba(1, 1, 1, 0.1)" @click.stop="navigateToVDP(vehicle)">
                <img
                    v-if="['1052','1053','1057','1059','1061','1063','1064'].includes(vehicle.advertiser_account_id)"
                    :src="'https://landing.carscience.com/assets/images/partner_logos/' + vehicle.advertiser_account_id + '.png'"
                    style="width: 96px"
                />
                <div v-else class="font-bold">
                  View Listing
                </div>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="text-primary">
                  <path d="M7 7h10v10"></path>
                  <path d="M7 17L17 7"></path>
                </svg>
              </div>
            </div>
          </div>
        </tw-card>

        <div v-if="adSenseEnabled && adSenseRandomizer >= 0.05 && (singleAdSenseAd ? i == 5 : i % 6 == 0)" :ref="'AdSense_' + i" class="adsense-ad-container mt-4"></div>
        <tw-card
          v-else-if="adSenseEnabled && (singleAdSenseAd ? i == 5 : i % 6 == 0)"
          :ref="'AdSense_' + i"
          no-padding
          class="vehicle-listings__card min-w-0 cursor-pointer mt-4"
          :dark-mode="darkMode"
        >
          <div class="adsense-ad-container"></div>
        </tw-card>
      </div>
    </div>

    <div v-if="loading && !darkMode" class="flex justify-center grid gap-4 grid-cols-1 md:grid-cols-2 mt-4">
      <tw-card v-for="i in perPage" :key="i + '_ad'" :ref="'VehicleListingsSkeleton_' + i" no-padding>
        <div class="animate-pulse flex flex-col" style="border-radius: 7px">
          <div class="flex-shrink-0 bg-gray-300 h-80 w-full" style="border-top-left-radius: 7px; border-top-right-radius: 7px" />
          <div class="flex flex-col w-full p-4">
            <div class="h-3 bg-gray-300 rounded w-5/12" />
            <div class="h-3 bg-gray-300 rounded w-full mt-6" />
            <div class="h-3 bg-gray-300 rounded w-full mt-2" />
          </div>
        </div>
      </tw-card>
    </div>
    <div v-if="loading && darkMode" class="flex justify-center grid gap-4 grid-cols-1 md:grid-cols-2 mt-4">
      <tw-card v-for="i in perPage" :key="i + '_ad'" :ref="'VehicleListingsSkeleton_' + i" flat no-padding :dark-mode="darkMode" style="background: transparent">
        <div class="animate-pulse flex flex-col">
          <div class="flex-shrink-0 w-full" style="aspect-ratio: 4/3; background: #242526" />
          <div class="flex flex-col w-full p-4">
            <div class="h-3 rounded" style="background: #242526" />
            <div class="h-3 bg-gray-300 rounded w-3/12 mt-1.5" style="background: #242526" />
            <div class="flex items-center justify-between mt-6">
              <div class="h-3 rounded w-4/12" style="background: #242526" />
              <div class="h-3 rounded w-3/12" style="background: #242526" />
            </div>
          </div>
        </div>
      </tw-card>
    </div>
    <div class="vehicle-listings__pagination pt-2 mt-4 mb-2" v-show="listings.length > 0 && !hideSeeMore">
      <infinite-scroll-observer v-if="infiniteScroll" @intersect="getVehicles()" />
      <tw-button
        v-else
        label="Show More"
        full-width
        @click.native="getVehicles()"
        :disabled="loading || disableSeeMore"
        :loading="loading"
      />
    </div>

    <p v-if="listings.length > 0" class="disclaimer-text --small mt-4">
      Title, other fees, and incentives are not included in these calculations, which are estimates only. Monthly payment estimates are for illustrative purposes only and do not represent a financing offer from the seller. Other taxes may apply.
    </p>

    <tw-modal v-if="showConfirmationModal" close-modal-style="close-icon" @close-modal="() => { showConfirmationModal = false; }" style="border-radius: 16px">
      <template v-slot:default>
        <div class="text-center p-4">
          <img
            :src="'https://landing.carscience.com/assets/images/partner_logos/' + confirmationVehicle.advertiser_account_id + '.png'"
            style="width: 200px"
            class="text-center w-full mx-auto mb-3"
          />
          <p
              style="font-size: 18px; margin: 0; padding: 0 24px; text-align: center; font-weight: 500"
          >
            We are sending you to <b>{{ confirmationAdvertiserNameMap[confirmationVehicle.advertiser_account_id] }}</b> to view the vehicle's details...
          </p>
          <div class="mt-3">
            <tw-button
              :label="'Continue to ' + confirmationAdvertiserNameMap[confirmationVehicle.advertiser_account_id]"
              full-width
              @click.native="navigateToVDP(confirmationVehicle)"
              color="secondary"
              style="text-decoration: none !important;"
            />
          </div>
        </div>
      </template>
    </tw-modal>

  </div>
</template>

<script>
import PublisherBase from "@/mixins/Publisher/PublisherBase";
import VehicleListingsExtension from "@/mixins/Publisher/VehicleListingsExtension";
import axios from "axios";

import TwCard from "@/components/Tailwind/TwCard.vue";
import TwImageCarousel from "@/components/Tailwind/TwImageCarousel.vue";
import IconMapMarker from "@/components/Icon/IconMapMarker.vue";
import IconHelp from "@/components/Icon/IconHelp.vue";
import TwButton from "@/components/Tailwind/TwButton.vue";
import InfiniteScrollObserver from "@/components/UI/InfiniteScrollObserver.vue";
import TwModal from "@/components/Tailwind/TwModal.vue";
import IconStorefront from "@/components/Icon/IconStorefront.vue";
import IconArrowNorth from "@/components/Icon/IconArrowNorth.vue";
import IconArrowNortheast from "@/components/Icon/IconArrowNortheast.vue";
import IconArrowEast from "@/components/Icon/IconArrowEast.vue";
import IconArrowSoutheast from "@/components/Icon/IconArrowSoutheast.vue";
import IconSteeringWheel from "@/components/Icon/IconSteeringWheel.vue";
import IconTransmission from "@/components/Icon/IconTransmission.vue";
import IconGasPump from "@/components/Icon/IconGasPump.vue";
import TwTooltipNew from "@/components/Tailwind/TwTooltipNew.vue";
import TwImage from "@/components/Tailwind/TwImage.vue";
import IconClose from "@/components/Icon/IconClose.vue";
import TwScoreBadge from "@/components/Tailwind/TwScoreBadge.vue";
import IconStarFilled from "@/components/Icon/IconStarFilled.vue";

export default {
  name: "VehicleListingsStandard",
  components: {
    IconStarFilled,
    TwScoreBadge,
    IconClose,
    TwImage,
    TwTooltipNew,
    IconGasPump,
    IconTransmission,
    IconSteeringWheel,
    IconStorefront,
    TwModal, InfiniteScrollObserver, TwButton, IconHelp, IconMapMarker, TwImageCarousel, TwCard },
  mixins: [
    PublisherBase,
    VehicleListingsExtension
  ],
  props: {
    ...PublisherBase.props,
    ...VehicleListingsExtension.props,
    advertiserAccountIds: {
      type: String,
      default: "1006,1024,1030,1031,1052,1053,1057,1059,1060,1061,1063"
    },
    country: {
      type: String,
      default: "us"
    },
    postalCode: {
      type: String,
      default: ""
    },
    make: {
      type: String,
      default: ""
    },
    model: {
      type: String,
      default: ""
    },
    bodyType: {
      type: String,
      default: ""
    },
    minPrice: {
      type: [ Number, String ],
      default: 0
    },
    maxPrice: {
      type: [ Number, String ],
      default: 100000
    },
    minYear: {
      type: [ Number, String ],
      default: 0
    },
    maxYear: {
      type: [ Number, String ],
      default: new Date().getFullYear() + 1
    },
    minMileage: {
      type: [ Number, String ],
      default: 0
    },
    maxMileage: {
      type: [ Number, String ],
      default: 1000000
    },
    radius: {
      type: [ Number, String ],
      default: 150
    },
    sortBy: {
      type: String,
      default: ""
    },
    topProductId: {
      type: [ Number, String ],
      default: ""
    },
    headerName: {
      type: String,
      default: "Recommended"
    },
    darkMode: {
      type: Boolean,
      default: false
    },
    infiniteScroll: {
      type: Boolean,
      default: true
    },
    enableAdSense: {
      type: Boolean,
      default: false
    },
    singleAdSenseAd: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      id: 2,
      adSenseRandomizer: Math.random(),
      showConfirmationModal: false,
      confirmationVehicle: null,
      confirmationAdvertiserNameMap: {
        "1052": "CarGurus.com",
        "1053": "CarMax.com",
        "1057": "UsedCars.com",
        "1058": "the Dealership Site",
        "1059": "CarMax.com",
        "1060": "the Dealership Site",
        "1061": "DriveTime.com",
        "1063": "CARFAX.com",
        "1064": "CarZing.com"
      },
      topProductOutOfRange: false,
      hideTopProductOutOfRange: false,
      topProductUnavailable: false,
      hideTopProductUnavailable: false,
      featuredListingProductIds: [],
      searchLocationFormatted: ""
    };
  },
  computed: {
    headerText() {
      return (this.headerName.length > 0 ? this.headerName + " " : "") + "Vehicles Near You";
    },
    adSenseEnabled() {
      return this.enableAdSense && this.domain == "carscience.com";
    }
  },
  watch: {
    listings() {
      // this.initGoogleAds();
    }
  },
  methods: {
    componentInit() {
      return new Promise((resolve) => {
        if (this.adSenseEnabled) {
          let s = document.createElement("script");
          s.src = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-7316900779987958";
          s.async = true;
          s.crossOrigin = "anonymous";
          document.getElementsByTagName('head')[0].appendChild(s);
        }

        // Configure vehicles request
        this.domElement.filters = [
          { country_code: { equals: this.country } },
          { price: { gte: this.minPrice, lte: this.maxPrice } },
          { year: { gte: this.minYear, lte: this.maxYear } },
          { mileage: { gte: this.minMileage, lte: this.maxMileage } }
        ];
        this.domElement.scoring = [];

        this.domElement.sorting = [
          { _score: "desc" },
          { bid_value: "desc" },
          { deal_score: "desc" }
        ];
        if(this.sortBy.length > 0) {
          let sortArray = this.sortBy.split("_");
          this.domElement.sorting = [
            { _score: "desc" },
            { [sortArray[0]]: sortArray[1] },
            { bid_value: "desc" },
            { deal_score: "desc" }
          ];
        }
        this.domElement.options = {
          exclude_fields: ["description", "features"]
        };

        if(this.advertiserAccountIds.length > 0) {
          this.domElement.filters.push({ advertiser_account_id: { in: this.advertiserAccountIds.split(",") } });
        }
        if(this.bodyType.length > 0) {
          let bodyTypeFilter = { equals: this.bodyType };
          if(this.bodyType.toLowerCase() === "car") {
            bodyTypeFilter = { in: ["sedan","coupe","hatchback","wagon","convertible"] };
          }
          this.domElement.filters.push({ body_type: bodyTypeFilter });
        }

        /*if(this.sortBy === "") {
          this.domElement.scoring.push({ 10: { mileage: { lte: 75000 } } });
          this.domElement.scoring.push({ 10: { price: { lte: 31000 } } });
        }*/
        this.domElement.scoring.push({ 20: { 'c_images.iv_conf': { gt: 0.5 }, 'c_images.no_conf': { gt: 0.5 } } });

        if(this.make.length > 0) {
          this.domElement.scoring.push({ 50: { make: { equals: this.make } } });
        }
        if(this.model.length > 0) {
          this.domElement.scoring.push({ 100: { model: { equals: this.model } } });
        }

        if(this.urlParams.get("cfx") == 1) {
          this.domElement.scoring.push({ 4000: { advertiser_account_id: { equals: 1063 } } });
        }

        this.searchLocationFormatted = this.visitorLocation.formatted;
        if(this.postalCode.length == 5 || this.visitorLocation.postalCode.length == 5) {
          this.domElement.options.location_search = {
            postal_code: this.postalCode.length > 4 ? this.postalCode : this.visitorLocation.postalCode,
            radius: this.radius, // TODO: Default this on api side if not set?
            use_payable_radius: true
          };

          if (this.postalCode.length > 4) {
            this.getPostalCodeLocationData(this.postalCode).then((locationData) => { this.searchLocationFormatted = locationData.city + ", " + locationData.state; });
          }
        }

        if(this.topProductId.toString().length > 0) {
          this.domElement.options.independent_filters = [
            { product_id: { equals: this.topProductId } }
          ];
          /*if(this.urlParams.get("fpid") == 1 || this.urlParams.get("fpid2") == 1) {
            this.domElement.options.independent_filters = [
              { product_id: { equals: this.topProductId } }
            ];
          }
          else {
            this.domElement.filters = [
              {
                or: [
                  { product_id: { equals: this.topProductId } },
                  {
                    and: this.domElement.filters
                  }
                ]
              }
            ];
          }*/
          this.domElement.scoring.push({ 5000: { "product_id": { "equals": this.topProductId } } });
        }

        let postVehicleLoadCallback = () => {
          if(this.adSenseEnabled) {
            // this.initGoogleAds();
          }
          this.restoreScrollPosition();

          if((this.urlParams.get('oor') == 1 || this.urlParams.get('fpid') == 1) && this.topProductId.toString().length > 0 && this.listings.length > 0 && this.topProductId != this.listings[0].product_id) {
            this.topProductUnavailable = true;
          }
          else if(this.urlParams.get('oor') == 1 && this.listings.length > 0 && this.topProductId == this.listings[0].product_id && this.listings[0].distance > 100) {
            this.topProductOutOfRange = true;
          }
        };

        // If srtLgc=1, fetch the top product and build scoring based on its attributes
        let srtLgcPromise = Promise.resolve();
        if(this.urlParams.get('srtLgc') == 1 && this.topProductId.toString().length > 0) {
          srtLgcPromise = axios.post("https://api-v2.jumpfeed.com/network/publisher/products/vehicles", {
            filters: [{ product_id: { equals: this.topProductId } }],
            pagination: { page: 1, per_page: 1 }
          })
            .then((response) => {
              if(response.data.data.results.length > 0) {
                let topVehicle = response.data.data.results[0];

                // Build scoring clauses based on vehicle attributes
                // Priority: Body Type -> Make -> Model -> Trim -> Price Range -> Exterior Color
                if (topVehicle.body_type) {
                  this.domElement.scoring.push({ 250: { body_type: { equals: topVehicle.body_type } } });
                }
                if (topVehicle.make) {
                  this.domElement.scoring.push({ 200: { make: { equals: topVehicle.make } } });
                }
                if (topVehicle.model) {
                  this.domElement.scoring.push({ 150: { model: { equals: topVehicle.model } } });
                }
                if (topVehicle.trim) {
                  this.domElement.scoring.push({ 500: { trim: { equals: topVehicle.trim } } });
                }
                // Price can come back as a string, so coerce it before doing any math
                // (string + number concatenates, which produced huge lte values)
                let topVehiclePrice = parseFloat(String(topVehicle.price ?? "").replace(/[^0-9.]/g, ""));
                if (Number.isFinite(topVehiclePrice) && topVehiclePrice > 0) {
                  let priceRange = topVehiclePrice * 0.22;
                  this.domElement.scoring.push({ 300: { price: { gte: Math.floor(Math.max(0, topVehiclePrice - priceRange)), lte: Math.ceil(topVehiclePrice + priceRange) } } });
                }
                if (topVehicle.exterior_color) {
                  this.domElement.scoring.push({ 100: { exterior_color: { equals: topVehicle.exterior_color } } });
                }
              }
            })
            .catch((error) => {
              console.log(error);
            });
        }

        // If awFll=1, fetch the Autoweb (1058) featured listing and boost it into the top slot
        let awFeaturedListingPromise = Promise.resolve();
        if(this.urlParams.get('awFll') == 1) {
          awFeaturedListingPromise = axios.post("https://api-v2.jumpfeed.com/network/publisher/products/vehicles", {
            filters: [{ advertiser_account_id: { equals: 1058 } }, { or: [{ make: { equals: this.make } }, { model: { equals: this.model } }, { body_type: { equals: this.bodyType } }]}],
            scoring: [{ 150: { model: { equals: this.model } } }, { 100: { make: { equals: this.make } } }, { 50: { body_type: { equals: this.bodyType } } }, { 25: { condition: { equals: "Used" } } }],
            pagination: { page: 1, per_page: 1 },
            sorting: [{ _score: "desc" }, { bid_value: "desc" }, { deal_score: "desc" }],
            options: {
              location_search: {
                postal_code: this.postalCode.length > 4 ? this.postalCode : this.visitorLocation.postalCode,
                radius: this.radius,
                use_payable_radius: true
              }
            }
          })
              .then((response) => {
                if(response.data.data.results.length > 0) {
                  let featuredListing = response.data.data.results[0];

                  this.domElement.filters = [
                    {
                      or: [
                        { product_id: { equals: featuredListing.product_id } },
                        {
                          and: this.domElement.filters
                        }
                      ]
                    }
                  ];

                  this.domElement.scoring.push({ 2000: { "product_id": { "equals": featuredListing.product_id } } });

                  this.featuredListingProductIds.push(featuredListing.product_id);
                }
              })
              .catch((error) => {
                console.log(error);
              });
        }

        // srtLgc and awFll are independent: when both params are set, the featured listing
        // still takes the top slot and the srtLgc scoring sorts the rest of the results
        Promise.all([srtLgcPromise, awFeaturedListingPromise]).then(() => {
          this.getVehicles().then(postVehicleLoadCallback);
          resolve();
        });
      });
    },
    promptConfirmationModal(vehicle) {
      this.confirmationVehicle = vehicle;
      this.showConfirmationModal = true;
    },
    initGoogleAds() {
      this.shadowDOM.querySelectorAll(".adsense-ad-container").forEach(adSenseContainer => {
        if (adSenseContainer.innerHTML === "") {

          if (this.adSenseRandomizer < 0.05) {
            adSenseContainer.innerHTML = '<ins class="adsbygoogle" style="display:block" data-ad-format="fluid" data-ad-layout-key="-6h+ej+14-3z+52" data-ad-client="ca-pub-7316900779987958" data-ad-slot="8206670720"></ins>';
          } else {
            adSenseContainer.innerHTML = '<ins class="adsbygoogle" style="display:block" data-ad-client="ca-pub-7316900779987958" data-ad-slot="4424719370" data-ad-format="auto" data-full-width-responsive="true"></ins>';
          }

          (window.adsbygoogle = window.adsbygoogle || []).push({});
          this.jfpTrack("ContentView", {
            advertiser_account_id: 1051,
            // advertiser_channel_1: XXX,
            // advertiser_channel_2: XXX,
            // advertiser_channel_3: XXX
          });
        }
      });
    },
    restoreScrollPosition() {
      let lastScrollPosition = window.sessionStorage.getItem(window.location.hostname + "-lastScrollPosition");
      if (lastScrollPosition !== null) {
        setTimeout(() => {
          window.scrollTo({
            top: lastScrollPosition,
            behavior: "instant"
          });
        }, 100);
      }
    },
    getDealScoreColor(dealScorePhrase) {
      if (dealScorePhrase == "excellent" || dealScorePhrase == "great") {
        return "#43e46c";
      } else if (dealScorePhrase == "good") {
        return "#28ae60";
      } else if (dealScorePhrase == "fair") {
        return "#F2C037";
      } else {
        return "#EF4444";
      }
    },
    getDealScoreArrow(dealScorePhrase) {
      if (dealScorePhrase == "excellent" || dealScorePhrase == "great") {
        return IconArrowNorth;
      } else if (dealScorePhrase == "good") {
        return IconArrowNortheast;
      } else if (dealScorePhrase == "fair") {
        return IconArrowEast;
      } else {
        return IconArrowSoutheast;
      }
    }
  }
}
</script>

<style src="@/styles/index.css" />

<style scoped></style>
